<script setup lang="ts">
import { ref, shallowRef, reactive, computed, watch, onMounted, onUnmounted, defineAsyncComponent } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table"
import { Pencil, Save, X, XCircle, Trash, Settings, Glasses, Download, File, Check } from "lucide-vue-next"
import axios from "axios"
import { useToast, Toaster } from "@/components/ui/toast"
import { useI18n } from 'vue-i18n';
import draggable from "vuedraggable"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import SingleSelect from './SingleSelect.vue'
import ManySelect from './ManySelect.vue'
const TiptapEditor = defineAsyncComponent(() => import('./TiptapEditor.vue'))
const EditorPanel = defineAsyncComponent(() => import('./EditorPanel.vue'))
import ColumnSelect from './ColumnSelect.vue'
import { storeToRefs } from "pinia"
import { useEntityStore } from "@/stores/useEntityStore"
import { useViewStore } from "@/stores/viewStore"
import { useColumnStore } from "@/stores/columnStore"
import { optionLabel, capitalize, get_data_attr } from '@/utils/globals'
import { useEventListener, useDebounceFn } from "@vueuse/core"

const { t, locale } = useI18n();
const viewStore = useViewStore();
const userInfo = get_data_attr("user");

const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
if (token) axios.defaults.headers.common["X-CSRF-Token"] = token

// Set initial locale header
axios.defaults.headers.common['X-Locale'] = locale.value || 'de'

// Map i18n locale to date locale string
const dateLocale = computed(() => {
  const localeMap: Record<string, string> = {
    'de': 'de-DE',
    'en': 'en-US',
    'zh-TW': 'zh-TW'
  }
  return localeMap[locale.value] || 'de-DE'
})

interface ColumnDef {
  key: string
  label: string
  type?: "text" | "number" | "date" | "color" | "select" | "boolean" | "select" | "multi-select" | "multi"
  write?: boolean
  required?: boolean
  options?: any[]
  optionsEndpoint?: string
  width?: number
}

const props = withDefaults(defineProps<{
  columns: any[]
  label: string
  labelSingular?: string
  logo?: string | null
  endpoint?: string
  saveendpoint?: string
  endpointpars?: Record<string, number>
  enableSearch?: boolean
  enableAdd?: boolean
  enableEdit?: boolean
  enableDetail?: boolean
  enableDelete?: boolean
  enableFilter?: boolean
  enableColChange?: boolean
  enableColFilterSide?: boolean
  enableColFilterTop?: boolean
  enableAll?: boolean
  enableCopy?:boolean
  enableNotifications?:boolean
  alwaysEdit?: boolean
  withpaging?: boolean
  hideActionColumn?: boolean
  headerFontSize?: number
  singleSelectFontsize?: number
  data?: any[] 
  onDelete?: (id: number) => void
  /** When set, detail/editor panel uses these columns instead of the table column list (e.g. full spec, read-only). */
  editorPanelColumns?: any[]
  /** Detail modal: hide Save, read-only presentation (still require columns with write: false or use editorPanelColumns). */
  editorReadOnly?: boolean
  ownColumnKeys?: string[]
}>(), {
  editorPanelColumns: undefined,
  editorReadOnly: false,
  ownColumnKeys: () => [],
  saveendpoint: null,
  logo: null,
  enableSearch: false,
  enableAdd: true,
  enableEdit: true,
  enableDetail: true,
  enableDelete: true,
  enableFilter: true,
  enableColChange: true,
  enableColFilterSide: true,
  enableColFilterTop: false,
  enableAll: false,
  alwaysEdit: false,
  enableCopy: true,
  enableNotifications: false,
  withpaging: true,
  hideActionColumn: false,
})

props.columns.forEach(col => {
  col.width = col.width ? parseInt(col.width) : 150
})

const columnStore = useColumnStore()

/** Build the current spec column list (actions + props.columns + buffer). */
function buildSpecColumns() {
  const spec = [
    ...(props.hideActionColumn
      ? []
      : [{ key: "actions", label: t("view.actions"), fixed: true }]),
    ...props.columns.map(c => ({ ...c })),
    // { key: "buffer", label: "Puffer" }
  ]

  if (props.endpoint && String(props.endpoint).includes('pflichten-sub-assignment-')) {
    console.log('[DataTable buildSpecColumns SNAPSHOT]', JSON.parse(JSON.stringify(spec)))
  }

  return spec
}

/** Merge saved column state with current spec: preserve saved order and user prefs (show, width), add new spec columns at end. */
function mergeColumnsWithSpec(specColumns: any[], saved: any[]) {
  const specByKey = new Map(specColumns.map((c: any) => [c.key, c]))
  const savedKeys = new Set(saved.map((c: any) => c.key))
  const merged: any[] = []
  // Preserve saved order and user preferences for existing columns; refresh definition (label, type, options) from spec.
  // Always take show/width from saved so spec (e.g. props.columns) never overwrites user toggles.
  for (const savedCol of saved) {
    const specCol = specByKey.get(savedCol.key)
    if (!specCol) continue // column removed from spec, skip
    merged.push({
      ...specCol,
      ...savedCol,
      label: specCol.label,
      type: specCol.type,
      options: specCol.options,
      display_options: specCol.display_options,
      required: specCol.required,
      write: specCol.write,
      optionsEndpoint: specCol.optionsEndpoint,
      show: Object.prototype.hasOwnProperty.call(savedCol, 'show') ? savedCol.show : (specCol.show !== false),
      width: Object.prototype.hasOwnProperty.call(savedCol, 'width') ? savedCol.width : (specCol.width ?? 150)
    })
  }
  // Append new columns from spec (not in saved) in spec order, with defaults
  for (const specCol of specColumns) {
    if (savedKeys.has(specCol.key)) continue
    merged.push({ ...specCol, show: specCol.show !== false, width: specCol.width ?? 150 })
  }
  return merged
}

const specColumnsComputed = computed(() => buildSpecColumns())

const columns = computed({
  get() {
    // Skip column store for tables without an endpoint (data-only, e.g. assignment matrices)
    // and for assignment panels whose endpoint contains '-assignment-'.
    const isAssignmentPanel = !props.endpoint || String(props.endpoint).includes('-assignment-')
    const saved = isAssignmentPanel ? null : columnStore.getColumns(props.endpoint)
    const specColumns = specColumnsComputed.value

    if (!saved) {
      const initial = specColumns
      if (!isAssignmentPanel) columnStore.setColumns(props.endpoint, initial)
      return initial
    }

    const merged = mergeColumnsWithSpec(specColumns, saved)
    // Do not write merged back here: order and show are updated by setter (reorder) and onToggleColumn (toggle).
    // Writing here would overwrite those updates with a new array reference and could revert to stale state.
    return merged
  },
  set(val) {
    const isAssignmentPanel = !props.endpoint || String(props.endpoint).includes('-assignment-')
    if (!isAssignmentPanel) columnStore.setColumns(props.endpoint, val)
  }
})

/** Columns passed to EditorPanel (glasses): optional full spec for task-linked RG, else current table columns. */
const editorColumnsForPanel = computed(() => {
  const extra = props.editorPanelColumns
  if (extra && extra.length > 0) return extra
  return columns.value
})

const editorPanelTitle = computed(() => {
  const lbl = props.label
  if (props.editorReadOnly) {
    return typeof lbl === "string" && lbl.startsWith("view.") ? t(lbl) : String(lbl ?? "")
  }
  return t(lbl) + " bearbeiten"
})

const visibleColumns = computed(() => {
  void columnToggleSeq.value
  return columns.value.filter(c => c.show || c.key === 'actions')
})

watch(() => props.columns, (newCols) => {
  newCols.forEach((col) => {
    const localCol = columns.value.find(c => c.key === col.key)
    if (localCol) {
      localCol.label = col.label
      localCol.type = col.type
      localCol.write = col.write
      localCol.width = col.width ?? localCol.width ?? 150
      localCol.custom = col.custom
    }
  })
}, { deep: true })

const rows = shallowRef<any[]>([])
const isLoading = ref(false)
const isSortLoading = ref(false)
const loadErrorMessage = ref("")
const editingRowId = ref<string | number | null>(null)
const editingRow = ref<Object>(null)
const isAddingRow = ref(false)
const validationErrors = ref<Record<string, string>>({})
const { toast } = useToast()

// Server-side state
const serverSearch = ref("")
const serverColumnFilters = ref<Record<string, any>>({})
const serverSortKey = ref<string | null>(null)
const serverSortAsc = ref(true)

// Look for a stored page or default to 1
const storageKey = `table-page-${props.endpoint}`;
const serverPage = ref(parseInt(sessionStorage.getItem(storageKey) || "1"));

// Watch for changes and save to storage
watch(serverPage, (newPage) => {
  sessionStorage.setItem(storageKey, newPage.toString());
});

const serverPageSize = ref(50)
const totalRows = ref(0)
const firstTime = ref(true);
const loadRequestSeq = ref(0)
let activeLoadAbortController: AbortController | null = null
// Dynamic select options
const dynamicOptions = ref<Record<string, any[]>>({})

const emit = defineEmits(['swapcol', 'page-loaded', 'cell-changed', 'cell-notified', 'data-mutated']);



function getNested(obj: any, path: string) {
	// console.log("assignment=" + JSON.stringify(obj["assignment"]))
  
	// console.log("getNested path=" + path)
  let psplit = path.split(".");

  let val = psplit.reduce((o, k) => (o ? o[k] : undefined), obj)

//   console.log("val=" + val)
  return psplit.reduce((o, k) => (o ? o[k] : undefined), obj)
}

function isBooleanValue(value: any): boolean {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "Ja" || value === "Yes") {
    return true
  }
  return false
}

function formatCellValue(value: any): any {
  if (value == null) return ""
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return value
}

function setNested(obj: any, path: string, value: any, event?: Event) {
  const keys = path.split(".");
  let o = obj;

  keys.slice(0, -1).forEach((k) => {
    if (!o[k]) o[k] = {};
    o = o[k];
  });

  o[keys[keys.length - 1]] = value;
  // Keep v-memo rows reactive: bump per-row version after every field edit.
  if (obj && obj.id != null) {
    rowVersions[obj.id] = (rowVersions[obj.id] || 0) + 1
  }

  emit("cell-changed", {
    id: obj.id,
    path: path,  // send as array
    value: value,
	event: event,
  });
}

async function uploadFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/kunde/', {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data.url; // e.g., the uploaded file URL returned by your backend
}

function onFileChange(event: Event, key: string, currentItem) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] || null;

  if (file) {
    // Store the file temporarily in your currentItem
    setNested(currentItem, key, file);
  } else {
    // Clear if no file selected
    setNested(currentItem, key, null);
  }
}


function onCheckboxChange(event: Event, row: any, key: string) {
  const input = event.target as HTMLInputElement;
  const newValue = input.checked;

  try {
    setNested(row, key, newValue, event);
  } catch (err) {
    console.error(err);
    input.checked = !newValue;
  }	
}


async function loadDynamicOptions() {
  for (const col of props.columns) {
    if (col.type === "select" && col.optionsEndpoint) {
      const { data } = await axios.get(col.optionsEndpoint)
      dynamicOptions.value[col.key] = data
    }
  }
}

function buildQueryParams() {
  const params: Record<string, any> = {
    page: serverPage.value,
    per_page: serverPageSize.value,
  }
  if (props.endpointpars) {
	for (const [key, value] of Object.entries(props.endpointpars)) {
	  if (value !== null && value !== "") params[key] = value
	}	
  }
  if (serverSearch.value) {
	if (serverSearch.value === "reset_filter") {
		params["reset_filter"] = 1
	} else if (serverSearch.value === "reset_filter_button") {
		params["reset_filter_button"] = 1
	} else {
		params.q = serverSearch.value		
	}
  } 
  if (serverSortKey.value) {
    params.sort_by = serverSortKey.value
    params.sort_order = serverSortAsc.value ? "0" : "1"
  }
  for (const [key, value] of Object.entries(serverColumnFilters.value)) {
    if (Array.isArray(value) && value.length === 0) {
      // Keep the key in query params so the backend can clear persisted session filters.
      params[key] = [""]
    } else if (value !== null && value !== "") {
      params[key] = value
    }
  }
  // Only apply backend "reset_filter" on the initial page load without any active search/sort/filter.
  // When the user starts typing fast, in-flight requests can race; this guard prevents accidentally
  // including reset_filter on subsequent (user-driven) requests while the initial load is unresolved.
  const hasAnyUserQuery =
    Boolean(serverSearch.value) ||
    Boolean(serverSortKey.value) ||
    Object.keys(serverColumnFilters.value).length > 0

  if (firstTime.value && !hasAnyUserQuery) {
    params["reset_filter"] = 1
  }
  return params
}


async function loadServerData() {
  loadErrorMessage.value = ""
  if (props.data) {
    console.log("just with dataprop=" + JSON.stringify(props.data.map(d => d.assignment)));
    rows.value = props.data.map(r => ({ ...r }))
    dataVersion.value++
    refreshDatascopeFilterOptions()
    totalRows.value = props.data.length;
    isLoading.value = false
    return
  }
  console.log("loadServerData()=" + props.endpoint)
  isLoading.value = true
  if (!isSortLoading.value) {
    rows.value = []
  }
  const requestId = ++loadRequestSeq.value

  // Abort the previous request to avoid "last response wins" racing when typing quickly.
  activeLoadAbortController?.abort()
  activeLoadAbortController = new AbortController()
  const signal = activeLoadAbortController.signal

  let data: any[] = []
  let headers: Record<string, any> = {}
  try {
    const res = await axios.get(props.endpoint + ".json", {
      params: buildQueryParams(),
      signal,
    })
    data = res.data
    headers = res.headers || {}
  } catch (err: any) {
    const isCanceled =
      Boolean(err?.name === "CanceledError") ||
      Boolean(err?.code === "ERR_CANCELED") ||
      Boolean(axios.isCancel?.(err))
    if (isCanceled) return
    console.error("loadServerData() failed", err)
    rows.value = []
    totalRows.value = 0
    const status = err?.response?.status
    if (status === 403) {
      loadErrorMessage.value = "403 Keine Berechtigung"
    } else {
      const rawMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.statusText ||
        err?.message ||
        "Fehler beim Laden"
      const message = typeof rawMessage === "string" ? rawMessage : "Fehler beim Laden"
      loadErrorMessage.value = status ? `${status} ${message}` : message
    }
    return
  } finally {
    // Only clear loading for the latest in-flight request.
    if (requestId === loadRequestSeq.value) isLoading.value = false
  }

  // If a newer request was triggered, ignore this response.
  if (requestId !== loadRequestSeq.value) return

  // Attach hitMap to each row
  rows.value = data.map((r: any) => ({ ...r }))
  isSortLoading.value = false
  refreshDatascopeFilterOptions()

  totalRows.value = parseInt(headers["x-total-count"] || String(data.length))
  firstTime.value = false
  console.log("emit page-loaded=" + data.length)
  emit('page-loaded', data);
}

watch(
  () => props.data,
  (newData) => {
    loadServerData();
  },
  { immediate: true }
);

// Watch locale changes to refetch data
watch(
  () => locale.value,
  (newLocale) => {
    axios.defaults.headers.common['X-Locale'] = newLocale
    // Refetch data when locale changes (only if using endpoint, not static data)
    if (props.endpoint && !props.data) {
      loadServerData()
    }
  }
)

// Refetch data when query parameters (e.g. id_rechtsgrundlagen) change.
// This is required for tabbed views: without it, the table would keep stale rows.
watch(
  () => (props.endpointpars ? JSON.stringify(props.endpointpars) : ''),
  () => {
    if (props.endpoint && !props.data) {
      if (consumeGlobalSearchNavigation()) {
        clearDatascopeFilter()
      } else {
        forceEigeneDatascopeFilter()
      }
      serverPage.value = 1
      loadServerData()
    }
  },
)



function sortBy(key: string) {
  if (serverSortKey.value === key) {
    serverSortAsc.value = !serverSortAsc.value
  } else {
    serverSortKey.value = key
    serverSortAsc.value = true
  }
  isSortLoading.value = true
//  serverPage.value = 1
  loadServerData() // only reload rows, not columns
}

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  serverPage.value = page
  loadServerData()
}

const debouncedLoadAfterFilter = useDebounceFn(() => {
  loadServerData()
}, 250, { maxWait: 1000 })

function onColumnFilterChange(key: string, value: any) {
  serverColumnFilters.value[key] = value
  serverPage.value = 1
  debouncedLoadAfterFilter()
}

function isFilterSet() {
  return Object.keys(serverColumnFilters.value).length > 0
}

function getFilterButtonColor() {
  let color = isFilterSet() ? 'red' : 'green'
  return `text-${color}-600 hover:bg-${color}-50`
}

function getFilterButtonText() {
  return isFilterSet() ? t('view.reset_filter')  : t('view.cleared_filter')
}

const onSearchChange = useDebounceFn((value: string) => {
  serverSearch.value = value
  serverPage.value = 1
  loadServerData()
}, 300, { maxWait: 1000 })

function resetFilters() {
  selectedOptions.value = {}
  serverSearch.value = "reset_filter_button"
  serverColumnFilters.value = {}
  serverPage.value = 1
  loadServerData()
}

function filterWriteable(k, v) {
   if (String(props.endpoint) === "kunde" && k === "import_standort_id") return true
   return props.columns.find(c => c.key === k && c.write)
}

async function saveRow(row: any) {
  console.log("saveRow(row: any)=" + JSON.stringify(row))
  try {
    if (!row.id || row.isNew) {
      const res = await axios.post(props.endpoint + ".json", row)
      const msg = res?.data?.message
      toast({ title: "Saved", description: msg || "Row saved successfully." })
    } else {
      let rowid = row.id;
	  let filteredEntries = Object.entries(row).filter(([k, v]) => filterWriteable(k, v)); 
      const res = await axios.put(`${props.saveendpoint || props.endpoint}/${rowid}.json`, Object.fromEntries(filteredEntries))
      const msg = res?.data?.message
      toast({ title: "Saved", description: msg || "Row saved successfully." })
    }
    loadServerData()
    emit('data-mutated', props.endpoint)
    editingRowId.value = null
  } catch (error: any) {
	console.log("error=" + JSON.stringify(error.response))
	const message =
	  error.response?.data?.message ||
	  error.response?.data?.error ||
	  error.response?.data ||
	  error.response?.statusText ||
	  "Failed to save row."
    toast({ title: "Error", description: message, variant: "destructive" })
  }
}

function defaultVal4Col(col) {
	if (col.default) {
		return col.default
	}
    if (col.type === "number") {
	    return 0;
	}
    if (col.type === "many") {
	    return [];
	}
	return "";
}

function addRow() {
  // Special handling for gefahrstoffe - navigate to form page
  if (props.endpoint === 'gefahrstoffe') {
    viewStore.setActionView('registry.gefahrstoffe.new')
    return
  }
  
  const tempRow: any = { id: `new-${Date.now()}`, isNew: true }
  props.columns.forEach(col => tempRow[col.key] = defaultVal4Col(col))
  rows.value = [tempRow, ...rows.value]
  editingRowId.value = tempRow.id
  isAddingRow.value = true
}

async function editRow(row: any) {
  // Special handling for gefahrstoffe - navigate to form page
  if (props.endpoint === 'gefahrstoffe') {
    viewStore.setActionView(`registry.gefahrstoffe.edit.${row.id}`)
    return
  }
  
  editingRowId.value = row.id
  editingRow.value = JSON.parse(JSON.stringify(row))
}

async function deleteRow(id: number | string) {
	
  if (props.onDelete) {
	console.log("props.onDelete=" + id)
	return props.onDelete(id)
  }
  try {
	const confirmMsg =
	  props.endpoint === 'users'
	    ? t('view.user_set_inactive_confirm')
	    : t(props.label) + " Löschen? Kann nicht wiederrufen werden."
	const ok = await confirm(confirmMsg)
	if (ok) {
		await axios.delete(`${props.endpoint}/${id}.json`)
		loadServerData()
		emit('data-mutated', props.endpoint)
		if (props.endpoint === 'users') {
		  toast({ title: t('view.user_set_inactive_title'), description: t('view.user_set_inactive_desc') })
		} else {
		  toast({ title: "Deleted", description: `Row ${id} removed.` })
		}
	}	
	  isAddingRow.value = false
  } catch (error: any) {
	const message =
	error.response?.data?.error ||
	error.response?.data?.message || // if your API returns JSON with "message"
	error.response?.statusText ||   // fallback to status text
	"Failed to delete row."
    toast({ title: "Error", description: message, variant: "destructive" })
  }
}

function cancelEdit(row: any) {
  if (!row) return

  if (row.isNew) {
    rows.value = rows.value.filter(r => r?.id !== row.id)
  } else {
    const idx = rows.value.findIndex(r => r?.id === row.id)
    if (idx !== -1 && editingRow.value) {
      // Restore the original deep clone
      rows.value = rows.value.map((r, i) => i === idx ? JSON.parse(JSON.stringify(editingRow.value)) : r)
    } else {
      console.warn('cancelEdit: row not found or already removed', row)
    }
  }

  editingRowId.value = null
  editingRow.value = null
  isAddingRow.value = false
  validationErrors.value = {}
}

const resizingColumn = ref<ColumnDef | null>(null)
const startX = ref(0)
const startWidth = ref(0)
const tableRef = ref<HTMLElement | null>(null)

const stableKey = computed(() => props.endpoint ?? 'static')
const columnOrderKey = computed(() => columns.value.map(c => `${c.key}:${c.show}`).join(','))
const dataVersion = ref(0)
const rowVersions = reactive<Record<number, number>>({})
const columnToggleSeq = ref(0)
const tableBodyMessage = computed(() => {
  if (isLoading.value) return ""
  if (loadErrorMessage.value) return loadErrorMessage.value
  if (rows.value.length === 0) return "Keine Daten"
  return ""
})

const columnCssVars = computed(() => {
  const style: Record<string, string> = {}
  for (const col of columns.value) {
    if (col.key !== 'actions') {
      style[`--col-${col.key}-width`] = `${col.width ?? 150}px`
    }
  }
  return style
})

function startResize(e: MouseEvent, col: ColumnDef) {
  resizingColumn.value = col
  startX.value = e.clientX
  startWidth.value = col.width || 150

  window.addEventListener('mousemove', onResize)
  window.addEventListener('mouseup', stopResize)
}

function onResize(e: MouseEvent) {
  if (!resizingColumn.value) return
  const delta = e.clientX - startX.value
  resizingColumn.value.width = Math.max(50, startWidth.value + delta)
  tableRef.value?.style.setProperty(
    `--col-${resizingColumn.value.key}-width`,
    `${resizingColumn.value.width}px`
  )
}

async function stopResize() {
  if (resizingColumn.value) {
    // Call server-side action here with the updated width
    try {
	  await axios.post(`/${props.endpoint}/resizecol`, {
		idx: resizingColumn.value.sort,
        column: resizingColumn.value.key,
        width: resizingColumn.value.width
      })
      // Optionally show a toast
      //toast({ title: "Column resized", description: `${resizingColumn.value.label} updated.` })
	} catch (error: any) {
		const message =
		error.response?.data?.message ||
		error.response?.statusText ||
		"Failed to update column width"
      toast({ title: "Error", description: message, variant: "destructive" })
    }
  }

  resizingColumn.value = null
  window.removeEventListener('mousemove', onResize)
  window.removeEventListener('mouseup', stopResize)
}


const totalPages = computed(() => Math.ceil(totalRows.value / serverPageSize.value))

function consumeGlobalSearchNavigation() {
  const key = "globalsearch-nav"
  const v = sessionStorage.getItem(key)
  if (v) sessionStorage.removeItem(key)
  return Boolean(v)
}

function clearDatascopeFilter() {
  delete selectedOptions.value["datascope"]
  delete serverColumnFilters.value["datascope"]
}

const shouldForceEigeneDatascope = computed(() => {
  const endpoint = String(props.endpoint || "")
  const isHoeppnerUser = Boolean(userInfo?.is_hoeppner)
  if (isHoeppnerUser) return false
  return endpoint === "pflichten" || endpoint === "rechtsgrundlagen" || endpoint === "tasks"
})

function forceEigeneDatascopeFilter() {
  if (!shouldForceEigeneDatascope.value) return
  // datascope is a multi-select filter; backend expects datascope[]=1 for "Eigene"
  selectedOptions.value["datascope"] = [1]
  serverColumnFilters.value["datascope"] = [1]
}

onMounted(async () => {
  await loadDynamicOptions()
  if (consumeGlobalSearchNavigation()) {
    clearDatascopeFilter()
  } else {
    forceEigeneDatascopeFilter()
  }
  await loadServerData()
})

watch(() => props.endpoint, async () => {
  await loadDynamicOptions()
  if (consumeGlobalSearchNavigation()) {
    clearDatascopeFilter()
  } else {
    forceEigeneDatascopeFilter()
  }
  await loadServerData()
})


const onColumnReorder = async (evt: any) => {
  // evt contains oldIndex, newIndex

  let visibleColumns = props.columns.filter(c => c.show || c.key === 'actions')

  let from = evt['oldIndex'];
  let to   = evt['newIndex'];

  if (from === props.columns.length - 1 || to === props.columns.length - 1) {
	// never reorder last column
	return;
  }

  console.log("onColumnReorder props.columns=" + JSON.stringify(visibleColumns.map(c => [c.sort, c.key])))
  console.log("from=" + from + " to=" + to)
  try {
    await axios.post(`/${props.endpoint}/swapcols`, { from: from, to: to, fromId: visibleColumns[from - 1].key, toId: visibleColumns[to - 1].key})
	//emit('swapcol', props.endpoint, from, to)
    //toast({ title: "Saved", description: "Column order updated." })
  } catch (error: any) {
	const message =
		error.response?.data?.message ||
		error.response?.statusText ||
        "Failed to save column order."       
    toast({ title: "Error", description: message, variant: "destructive" })
  }
}

function formatISOForDateInput(val: string | undefined | null) {
  if (!val) return ''
  // If val contains a timestamp, strip it
  return val.split('T')[0]
}

const selectedOptions = ref<Record<string, any[]>>({})

// Pre-populate filters from column specs that carry a defaultFilter (e.g. datascope → [1] "Eigene").
// Only apply once per session/endpoint — after the user changes the filter, their choice is kept.
const defaultFilterAppliedKey = `defaultFilter-applied-${props.endpoint}`
if (!sessionStorage.getItem(defaultFilterAppliedKey)) {
  for (const col of props.columns) {
    if (col.defaultFilter && Array.isArray(col.defaultFilter) && col.defaultFilter.length > 0) {
      selectedOptions.value[col.key] = [...col.defaultFilter]
      serverColumnFilters.value[col.key] = [...col.defaultFilter]
    }
  }
  sessionStorage.setItem(defaultFilterAppliedKey, "1")
}

function toggleManyEdit(col, row, oid, val) {
	let k = col.key;
	if (val) {
	  let missingOption = col.options.find(opt => opt.id === oid);
	  row[k].push({...missingOption});
	} else {
	  row[k] = row[k].filter(opt => opt.id !== oid);
	}
	rowVersions[row.id] = (rowVersions[row.id] || 0) + 1
}

function toggleManyFilter(col, optionId, checked) {
	let colKey = col.key;
	if (!selectedOptions.value[colKey]) selectedOptions.value[colKey] = []
	if (checked) {
	  if (!selectedOptions.value[colKey].includes(optionId))
	    selectedOptions.value[colKey].push(optionId)
	} else {
	  selectedOptions.value[colKey] = selectedOptions.value[colKey].filter(id => id !== optionId)
	}
    onColumnFilterChange(colKey, selectedOptions.value[colKey])
}

function toSelectHash(options, arr, displayOptions = null) {
	console.log("JSON.stringify(options)=" + JSON.stringify(options));
	console.log("JSON.stringify(arr)=" + JSON.stringify(arr));
	if (arr == null || arr == "") {
		return {}
	}
	  return arr.reduce((acc, obj) => {
	    acc[obj.id] = optionLabel(options, obj, displayOptions);
	    return acc;
	  }, {});
}

function toFilterHash(options, arr, displayOptions = null) {
	if (arr == null) {
	    return [];
	} 
	
	console.log("arr=" + JSON.stringify(arr))
	return toSelectHash(options, arr.map(item => ({ id: item })), displayOptions)
}

function toCollection(list) {
  if (list == null) {
	return []
  }
  if (Array.isArray(list)) return list
  // ManySelect / view rendering expects an array of objects like `{ id }`.
  // Some backends store a single scalar (e.g. datascope = 0/1). Normalize it.
  if (typeof list !== "object") {
    return [{ id: list }]
  }
  return Object.values(list)
}

function refreshDatascopeFilterOptions() {
  const col = columns.value.find(c => c.key === "datascope")
  if (!col || (col.type !== "many" && col.type !== "multi-select")) return

  const byId = new Map<any, { id: any; label: string }>()
  rows.value.forEach((row) => {
    const values = toCollection(getNested(row, "datascope"))
    values.forEach((entry: any) => {
      const id = typeof entry === "object" ? entry.id : entry
      if (id == null) return
      const fallbackLabel = optionLabel(col.options || [], { id }, col.display_options)
      const label = (typeof entry === "object" && entry.label) ? entry.label : String(fallbackLabel)
      if (!byId.has(id)) byId.set(id, { id, label })
    })
  })

  // Keep the forced default "Eigene" option visible in the filter even when
  // the current page rows do not contain any datascope entry with id=1.
  if (shouldForceEigeneDatascope.value && !byId.has(1)) {
    const ownLabel = String(optionLabel(col.options || [], { id: 1, label: t('view.own') }, col.display_options))
    byId.set(1, { id: 1, label: ownLabel || t('view.own') })
  }

  const options = Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, "de"))
  col.options = [{ id: -1, label: "Keine" }, ...options]
}

const entityStore = useEntityStore()
const { currentItem, showEditor, editorSaving } = storeToRefs(entityStore)

defineExpose({ onColumnFilterChange });

function onToggleColumn() {
	// ColumnSelect mutates column.show in place; persist current columns so the getter does not overwrite on next read
	const isAssignmentPanel = !props.endpoint || String(props.endpoint).includes('-assignment-')
	if (!isAssignmentPanel) columnStore.setColumns(props.endpoint, columns.value)
	columnToggleSeq.value++
	loadServerData()
}

onUnmounted(() => {
  activeLoadAbortController?.abort()
})

const menuVisible = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const selectedCellRow = ref('')
const selectedCellCol = ref('')
const selectedCellContent = ref('')
const hoverCopyMenuItem = ref(false)
const hoverEmailMenuItem = ref(false)

function showContextMenu(event: MouseEvent, row : Object, col : Object, content: string) {

  console.log("showContextMenu row=" + row + " col=" + col + " content=" + content)
  // Only trigger for secondary button (right click)
  if (event.button !== 2) return
  event.preventDefault()
  event.stopPropagation()

  selectedCellRow.value = row
  selectedCellCol.value = col
  selectedCellContent.value = content

  // Prevent menu from going outside the viewport
  const menuWidth = 140
  const menuHeight = 36
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  menuX.value = event.clientX + menuWidth > viewportWidth ? viewportWidth - menuWidth - 10 : event.clientX
  menuY.value = event.clientY + menuHeight > viewportHeight ? viewportHeight - menuHeight - 10 : event.clientY

  menuVisible.value = true
}

function hideContextMenu() {
  menuVisible.value = false
}

function copyToClipboard() {
  navigator.clipboard.writeText(selectedCellContent.value).then(() => {
    toast({ title: "Copied", description: selectedCellContent.value })
    hideContextMenu()
  })
}

function notifyStatus() {

  emit("cell-notified", {
    id: selectedCellRow.value,
    path: selectedCellCol.value,
    value: selectedCellContent.value
  });
}

// Hide menu when clicking anywhere else
onMounted(() => window.addEventListener('click', hideContextMenu))
onUnmounted(() => window.removeEventListener('click', hideContextMenu))

// Computed property for add button text with correct word order
const addButtonText = computed(() => {
  const itemLabel = t(props.labelSingular || props.label)
  const addLabel = t('view.add')
  // English: "Add [Item]", German/Chinese: "[Item] hinzufügen/添加"
  return locale.value === 'en' ? `${addLabel.charAt(0).toUpperCase() + addLabel.slice(1)} ${itemLabel}` : `${itemLabel} ${addLabel}`
})


</script>

<template>
  <Card  :key="stableKey" class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex justify-between items-center p-4 bg-gray-50 border-b h-[65px]">
		<h2 class="text-xl font-bold flex items-center gap-2">
		  <slot name="logo" />
		  <span>{{ t(props.label)}}</span>
		</h2>
		<div v-if="props.enableColFilterTop" class="shrink-0 ml-4">
            <ColumnSelect :columns="columns" :endpoint="endpoint" @toggleColumn="onToggleColumn" :enableAll="enableAll" :ownColumnKeys="props.ownColumnKeys" />
		</div>
		<Toaster />
	  <div class="flex gap-2 items-center w-full ml-4">
		<slot name="extraglobalactions" />		
        <Input v-if="enableSearch" v-model="serverSearch" placeholder="Search..." @input="onSearchChange(($event.target as HTMLInputElement).value)" class="w-48" />
        <Button v-if="enableAdd" @click="addRow" class="greenbutton text-white">{{t(props.labelSingular || props.label) + " " + t('view.add')}}</Button>
      </div>
    </div>

    <!-- Table container -->
    <div ref="tableRef" class="flex-1 overflow-auto overflow-x-scroll relative" :style="columnCssVars" :aria-busy="isLoading ? 'true' : 'false'">
      <div
        v-if="isLoading && !isSortLoading"
        class="absolute left-0 right-0 bottom-0 top-[96px] z-[1500] flex items-center justify-center bg-white/60"
      >
        <div class="flex items-center gap-3 px-4 py-2 rounded-md bg-white shadow">
          <div class="datatable-spinner" aria-label="Loading" />
          <span class="text-sm text-gray-700">{{ t('view.loading') || 'Loading…' }}</span>
        </div>
      </div>
      <table class="min-w-max border-collapse table-auto">
		<thead>
		  <!-- Header row with draggable -->
		  <draggable
		    v-model="columns"
		    item-key="key"
		    tag="tr"
		    :animation="200"
		    :handle="'.drag-handle'"
		    :filter="'.fixed-column'"
		    @end="onColumnReorder"
		  >
		    <template #item="{ element }" v-for="element in columns" :key="element.key">
				<th
				  v-if="element.show !== false || (element.key === 'actions')"
				  class="sticky top-0 z-30 bg-gray-100 p-2 h-14 select-none border border-gray-200 relative"
				  :class="element.key === 'actions' ? 'left-0 z-1000 fixed-column' : ''"
				  :style="element.key === 'actions'
				    ? { width: '120px', minWidth: '120px', zIndex: 1000 }
				    : { width: `var(--col-${element.key}-width)`, minWidth: '50px',zIndex: 999, fontSize: props.headerFontSize ? props.headerFontSize + 'px' : undefined}"
				>

		        <!-- Only render content if column is visible or it's Actions -->
		        <div class="flex items-center justify-between relative">
		          <div v-if="element.key === 'actions'" class="font-semibold">
		            {{ t('view.actions') }}
		          </div>
				  <!-- <div v-else-if="element.key === 'datascope'" class="font-semibold">
				    {{ element.label }}
				  </div> -->
				  <div v-else-if="element.key === 'buffer'" class="font-semibold">
				  </div>
		          <div v-else class="flex items-center justify-between relative w-full gap-2">
					<button
					  type="button"
					  class="text-left flex-1 th-label"
					  @click="sortBy(element.key)"
					  :title="element.label"
					  :style="{overflow: 'unset', whiteSpace: 'nowrap'}"
					>
					  {{ element.label }}
					  <span v-if="serverSortKey === element.key" class="ml-1 text-xs text-gray-500">
					    <span
					      v-if="isSortLoading"
					      class="inline-block w-3.5 h-3.5 align-middle"
					      style="border: 2px solid rgba(0,0,0,0.15); border-top-color: rgba(0,0,0,0.55); border-radius: 50%; animation: datatable-spin 0.8s linear infinite;"
					      aria-label="Sortierung wird geladen"
					    ></span>
					    <span v-else>{{ serverSortAsc ? "▲" : "▼" }}</span>
					  </span>
					</button>

		            <!-- Drag handle -->
		            <span v-if="element.key !== 'actions' && enableColChange" class="drag-handle ml-2 cursor-move px-2 py-1" :title="t('view.draghandle')">≡</span>

		            <!-- Resize handle -->
		            <div
		              v-if="element.key !== 'actions' && enableColChange"
		              class="absolute top-0 right-0 h-full cursor-col-resize z-20 flex items-center justify-center"
		              style="width: 8px;" 
		              @mousedown.prevent="startResize($event, element)"
		            >
		              <div class="w-0.5 h-6 bg-gray-500 rounded" :title="t('view.resizehandle')"></div>
		            </div>

					<slot name="colextensions" :col="element" />

		          </div>
		        </div>
		      </th>
		    </template>
			<tr class="w-full"/>
		  </draggable>

		  <!-- Filter row -->
		  <tr>
		    <th
		      v-for="col in visibleColumns"
		      :key="col.key"
		      class="sticky top-12 z-30 bg-gray-50 p-1 h-12 min-w-[150px] border border-gray-200"
		      :class="col.key === 'actions' ? 'left-0 z-[1000] min-w-[130px]' : 'z-[999]'"
		      :style="{ width: col.width + 'px', minWidth: '50px'}"
			  :att="col.key"
		    >
		      <!-- Actions column filter cell -->
		      <template v-if="col.key === 'actions' && props.enableColFilterSide">
				<div class="flex items-center justify-center gap-2 h-full">
				<ColumnSelect :columns="columns" :endpoint="props.endpoint"  @toggleColumn="onToggleColumn" :enableAll="enableAll" :ownColumnKeys="props.ownColumnKeys"/>
				
				<Button v-if="props.enableFilter" :class="getFilterButtonColor()" variant="outline" size="sm" @click="resetFilters" :title="getFilterButtonText()">
				  <XCircle/>
				</Button>
                </div>
		      </template>

		      <!-- Normal filter cells -->
			  			  
			  <template v-if="(col.type === 'multi-select' || col.type === 'many') && props.enableFilter">
				<ManySelect :col="col"
				            :selectedOptions="toFilterHash(col.options, selectedOptions[col.key], col.display_options)"
							:dynamicOptions="dynamicOptions"
				            :toggleOption="(oid, val) => toggleManyFilter(col, oid, val)" />
			  </template>

			  <template v-else-if="col.type === 'select' && (col.options || dynamicOptions[col.key])  && props.enableFilter">
			    <select
			      class="border rounded p-1 text-sm w-full"
			      @change="onColumnFilterChange(col.key, $event.target.value)"
			    >
			      <option value="">{{ t('view.all') }}</option>
			      <option
			        v-for="opt in (col.display_options && col.display_options.length ? col.display_options : col.options) || dynamicOptions[col.key]"
			        :key="opt.id"
			        :value="opt.id"
			      >
			        {{ opt.label }}
			      </option>
			    </select>
			  </template>

			  <!-- Boolean / checkbox filter: use a select with All/Yes/No -->
			  <template v-else-if="(col.type === 'boolean' || col.edit === 'checkbox') && props.enableFilter">
			    <select
			      class="border rounded p-1 text-sm w-full"
			      @change="onColumnFilterChange(col.key, $event.target.value)"
			    >
			      <option value="">{{ t('view.all') }}</option>
			      <option value="1">Ja</option>
			      <option value="0">Nein</option>
			    </select>
			  </template>

			  <!-- File columns: do not show a filter input -->
			  <template v-else-if="(col.type === 'file' || col.edit === 'file') && props.enableFilter">
			    <!-- No filter for file-type columns -->
			  </template>

			  <template v-else-if="col.key !== 'actions' && col.key !== 'buffer'  && props.enableFilter">
			    <Input
			      v-model="serverColumnFilters[col.key]"
			      @input="onColumnFilterChange(col.key, $event.target.value)"
			      :placeholder="col.type === 'date' ? 'i.e. >01.01.2025' : t('view.filter_placeholder')"
			      class="w-full text-sm"
			    />
			  </template>

		    </th>
		  </tr>
		</thead>

		<tbody>
		  <template v-if="!isLoading && tableBodyMessage">
		    <tr>
		      <td
		        :colspan="Math.max(visibleColumns.length, 1)"
		        class="sticky left-0 border border-gray-200 p-4 text-left text-gray-600 bg-white z-[998]"
		      >
		        {{ tableBodyMessage }}
		      </td>
		    </tr>
		  </template>
		  <template v-else>
		    <tr
		      v-for="row in rows"
		      :key="row.id"
          v-memo="[editingRowId === row.id, row.id, columnOrderKey, columnToggleSeq, dataVersion, rowVersions[row.id]]"
		      class="transition-colors odd:bg-white even:bg-gray-50 hover:bg-blue-50"
		    >
		    <!-- Actions cell -->
		    <td v-if="!props.hideActionColumn"
		      class="sticky left-0 p-1 min-w-[120px] border border-gray-200 z-[999]" style="background-color: white;"
		    >
		      <div class="flex gap-2 justify-center">
		        <template v-if="editingRowId === row.id">
		          <Button :title="t('view.btn_save')" size="icon" variant="ghost" class="text-green-600 hover:bg-green-50" @click="saveRow(row)">
		            <Save class="w-4 h-4" />
		          </Button>
		          <Button :title="t('view.btn_cancel')" size="icon" variant="ghost" class="text-gray-600 hover:bg-gray-50" @click="cancelEdit(row)">
		            <X class="w-4 h-4" />
		          </Button>
		        </template>
		        <template v-else>
				  <slot name="extraactions" :row="row" :rowId="row.id"/>
		          <Button v-if="enableEdit" :title="t('view.btn_edit')" size="icon" variant="ghost" class="text-black-600 hover:bg-blue-50" @click="editRow(row)">
		            <Pencil class="w-4 h-4" />
		          </Button>
				  <Button v-if="enableDetail" :title="t('view.btn_detail')" size="icon" variant="ghost" class="text-black-600 hover:bg-blue-50" @click="entityStore.openEditor(props.endpoint, row)">
				    <Glasses class="w-4 h-4" />
				  </Button>
			      <Button v-if="enableDelete" :title="t('view.btn_delete')" size="icon" variant="ghost" class="text-red-600 hover:bg-red-50" @click="deleteRow(row.id)">
			        <Trash class="w-4 h-4" />
			      </Button>
		        </template>
		      </div>
		    </td>

		    <!-- Data cells -->
		    <TableCell
		      v-for="col in columns.filter(c => c.key !== 'actions' && c.show)"
		      :key="col.key"
		      class="align-middle border border-gray-200 px-2 max-w-[400px] h-[60px] max-h-[60px] min-h-[60px]"
		      :class="{
		        'text-right font-mono': col.type === 'number',
		        'text-center': col.type === 'boolean' || col.edit === 'checkbox',
                'bg-yellow-50': (editingRowId === row.id && col.write) || row[row.hitMap?.[col.key]] === 1 }"
		    >
		      <!-- Edit mode -->
		      <div v-if="(editingRowId === row.id || props.alwaysEdit) && col.write"
			  @contextmenu.prevent="showContextMenu($event, row.id, col.key, getNested(row, col.key))">
		        <template v-if="!col.type || (col.type === 'text' && col.edit !== 'richtext')">
		          <Input
		            :modelValue="getNested(row, col.key)"
		            @update:modelValue="val => setNested(row, col.key, val)"
		          />
		        </template>
				<template v-else-if="!col.type || (col.type === 'text' && col.edit === 'richtext')">
				  <div class="tiptap-cell">
				    <TiptapEditor
				      :model-value="getNested(row, col.key)"
				      @update:model-value="val => setNested(row, col.key, val)"
				    />
				  </div>
			    </template>
		        <template v-else-if="col.type === 'number'">
		          <input
		            type="number"
		            :value="getNested(row, col.key)"
		            @input="setNested(row, col.key, $event.target.valueAsNumber)"
		            class="border rounded p-1 text-sm w-full"
		          />
		        </template>

		        <template v-else-if="col.type === 'color'">
		          <input
		            type="color"
		            :value="getNested(row, col.key)"
		            @input="setNested(row, col.key, $event.target.value)"
		            class="h-8 w-12 p-0 border-none bg-transparent"
		          />
		        </template>

				<template v-else-if="col.type === 'date'">
					<input
					  type="date"
					  :value="formatISOForDateInput(getNested(row, col.key))"
					  @input="setNested(row, col.key, $event.target.value)"
					  class="border rounded p-1 text-sm w-full"
					/>
				</template>
		        <template v-else-if="col.type === 'boolean' || col.edit === 'checkbox'">
		          <input
		            type="checkbox"
		            :checked="isBooleanValue(getNested(row, col.key))"
		            @click="onCheckboxChange($event, row, col.key)"
		          />
		        </template>

		        <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
					<SingleSelect
					  :fontSize="props.singleSelectFontsize ? props.singleSelectFontsize : undefined"
					  :options="col.options"
					  :displayOptions="col.display_options"
					  :modelValue="getNested(row, col.key)"
					  @update:modelValue="val => setNested(row, col.key, val)"
					/>
		        </template>
				
				<template v-else-if="col.type === 'many'">
				    <ManySelect  :col="col"
					             :selectedOptions="toSelectHash(col.options, getNested(row, col.key), col.display_options)"
								 :dynamicOptions="dynamicOptions"
								 :toggleOption="(oid, val) => toggleManyEdit(col, row, oid, val)"	/>
				</template>
				

		        <p v-if="validationErrors[col.key]" class="text-xs text-red-500 mt-1">
		          {{ validationErrors[col.key] }}
		        </p>
		      </div>
		      <!-- View mode -->
		      <div v-else   @contextmenu.prevent="showContextMenu($event, row.id, col.key, getNested(row, col.key))">
		        <template v-if="col.type === 'boolean' || col.edit === 'checkbox'">
		          <div class="flex items-center justify-center">
		            <Check
		              v-if="isBooleanValue(getNested(row, col.key))"
		              class="w-5 h-5 text-green-600"
		            />
		            <X
		              v-else
		              class="w-5 h-5 text-red-600"
		            />
		          </div>
		        </template>
				<template v-else-if="col.type === 'text' && col.edit === 'richtext'">
					<Tooltip>
		            <TooltipTrigger>
		              <div class="truncate" :style="{ maxWidth: col.width + 'px' }">
					    <div class="richtext" v-html="row.content"></div>
		              </div>
		            </TooltipTrigger>
		            <TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
		              <div v-html="row.content"></div>
		            </TooltipContent>
		          </Tooltip>

				</template>

				
		        <template v-else-if="col.type === 'select'  || col.type === 'multi-select'">
					<SingleSelect :disabled="true"
					  :showNothing="!col.write && isAddingRow"
					  :fontSize="props.singleSelectFontsize ? props.singleSelectFontsize : undefined"
					  :options="col.options"
					  :displayOptions="col.display_options"
					  :modelValue="getNested(row, col.key)"
					  @update:modelValue="val => setNested(row, col.key, val)"
					/>
		        </template>

				<template v-else-if="col.type === 'many'">
					<!-- Special handling for gefahrstoffe h_phrases -->
					<template v-if="props.endpoint === 'gefahrstoffe' && col.key === 'h_phrases'">
						<Tooltip>
							<TooltipTrigger>
								<div class="text-xs" :style="{ maxWidth: col.width + 'px' }">
									<div v-for="hp in toCollection(getNested(row, col.key))" :key="hp.id" class="mb-1">
										<strong>{{ hp.h_code }}</strong> {{ hp.h_phrase }}
									</div>
								</div>
							</TooltipTrigger>
							<TooltipContent class="whitespace-pre-line text-xs max-w-[500px] break-words">
								<div v-for="hp in toCollection(getNested(row, col.key))" :key="hp.id" class="mb-2">
									<strong>{{ hp.h_code }}:</strong> {{ hp.h_phrase }}
								</div>
							</TooltipContent>
						</Tooltip>
					</template>
					<!-- Special handling for gefahrstoffe ghs_pictograms: show images, filter/edit stays "many" -->
					<template v-else-if="props.endpoint === 'gefahrstoffe' && col.key === 'ghs_pictograms'">
			          <div class="flex items-center justify-center gap-1 flex-wrap">
			            <template v-if="Array.isArray(getNested(row, 'ghs_pictograms_urls'))">
			              <a 
			                v-for="(url, idx) in getNested(row, 'ghs_pictograms_urls')"
			                :key="idx"
			                :href="url"
			                target="_blank"
			                class="p-1 hover:bg-blue-50 rounded transition-colors"
			                :title="col.label"
			              >
			                <img :src="url" :alt="col.label" class="w-8 h-8 object-contain" />
			              </a>
			            </template>
			            <File v-else class="w-5 h-5 text-gray-300" />
			          </div>
					</template>
					<!-- Default many handling -->
					<template v-else>
						<Tooltip>
							<TooltipTrigger>
								<div class="truncate" :style="{ maxWidth: col.width + 'px' }">
									{{toCollection(getNested(row, col.key)).map(u => optionLabel(col.options, u, col.display_options)).join(', ')}}
								</div>
							</TooltipTrigger>
							<TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
								{{toCollection(getNested(row, col.key)).map(u => optionLabel(col.options, u, col.display_options)).join(', ')}}
							</TooltipContent>
						</Tooltip>
					</template>
				</template>
				
				<!-- Special handling for h_codes column (if it exists as a separate column) -->
				<template v-else-if="props.endpoint === 'gefahrstoffe' && col.key === 'h_codes'">
					<Tooltip>
						<TooltipTrigger>
							<div class="text-xs" :style="{ maxWidth: col.width + 'px' }">
								<div v-for="hp in (row.h_phrases ? toCollection(row.h_phrases) : [])" :key="hp.id" class="mb-1">
									<strong>{{ hp.h_code }}</strong> {{ hp.h_phrase }}
								</div>
							</div>
						</TooltipTrigger>
						<TooltipContent class="whitespace-pre-line text-xs max-w-[500px] break-words">
							<div v-for="hp in (row.h_phrases ? toCollection(row.h_phrases) : [])" :key="hp.id" class="mb-2">
								<strong>{{ hp.h_code }}:</strong> {{ hp.h_phrase }}
							</div>
						</TooltipContent>
					</Tooltip>
				</template>

		        <template v-else-if="col.type === 'color'">
		          <div class="flex items-center gap-2">
		            <div class="w-4 h-4 rounded-full border" :style="{ backgroundColor: getNested(row, col.key) }"></div>
		            <span class="text-xs text-gray-600">{{ getNested(row, col.key) }}</span>
		          </div>
		        </template>

		        <template v-else-if="col.type === 'date'">
		          {{
		            getNested(row, col.key)
		              ? new Date(getNested(row, col.key)).toLocaleDateString(dateLocale, {
		                  day: "2-digit",
		                  month: "2-digit",
		                  year: "numeric",
		                })
		              : ""
		          }}
		        </template>
				<template v-else-if="col.type === 'datetime'">
				  {{
					getNested(row, col.key)
					  ? new Date(getNested(row, col.key)).toLocaleString(dateLocale, {
					      day: "2-digit",
					      month: "2-digit",
					      year: "numeric",
					      hour: "2-digit",
					      minute: "2-digit",
					      second: "2-digit", // optional — remove if you don't want seconds
					    })
					  : ""
				  }}
				</template>
		        <template v-else-if="col.type === 'text'">
		          <Tooltip>
		            <TooltipTrigger>
		              <div class="truncate" :style="{ maxWidth: col.width + 'px' }">
		                {{ formatCellValue(getNested(row, col.key)) }}
		              </div>
		            </TooltipTrigger>
		            <TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
		              {{ formatCellValue(getNested(row, col.key)) }}
		            </TooltipContent>
		          </Tooltip>
		        </template>

				<template v-else-if="col.edit === 'file' || col.type === 'file'">
		          <div class="flex items-center justify-center gap-1 flex-wrap">
		            <template v-if="Array.isArray(getNested(row, col.key))">
		              <a 
		                v-for="(url, idx) in getNested(row, col.key)"
		                :key="idx"
		                :href="url"
		                target="_blank"
		                class="p-1 hover:bg-blue-50 rounded transition-colors"
		                :title="col.label"
		              >
		                <img :src="url" :alt="col.label" class="w-8 h-8 object-contain" />
		              </a>
		            </template>
		            <template v-else-if="getNested(row, col.key)">
		              <a 
		                :href="getNested(row, col.key)"
		                target="_blank"
		                download
		                class="p-2 hover:bg-blue-50 rounded transition-colors"
		                :title="col.label"
		              >
		                <Download class="w-5 h-5 text-blue-600" />
		              </a>
		            </template>
		            <File v-else class="w-5 h-5 text-gray-300" />
		          </div>
		        </template>

		        <template v-else>
		          {{ formatCellValue(getNested(row, col.key)) }}
		        </template>
		      </div>
		    </TableCell>
		    </tr>
		  </template>
		</tbody>
      </table>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center p-4 bg-gray-50 border-t h-[65px]">
      <div class="flex gap-2 items-center">
        <label v-if="withpaging" class="text-sm">{{t('view.entriesperpage')}}:</label>
        <select v-if="withpaging" v-model.number="serverPageSize" @change="loadServerData" class="border rounded p-1 text-sm">
          <option :value="5">5</option>
          <option :value="10">10</option>
          <option :value="20">20</option>
		  <option :value="50">50</option>
	      <option :value="100">100</option>
        </select>
      </div>
      <div v-if="withpaging" class="flex gap-2 items-center">
        <Button :style="{backgroundColor: serverPage === 1 ? 'gray' : 'black'}" size="sm" @click="goToPage(serverPage-1)">{{t('view.prev')}}</Button>
        <span class="text-sm">{{t('view.page')}} {{ serverPage }} {{t('view.of')}} {{ totalPages }} ({{totalRows}} {{t('view.entries')}})</span>
        <Button :style="{backgroundColor: serverPage === totalPages ? 'gray' : 'black'}" size="sm" @click="goToPage(serverPage+1)">{{t('view.next')}}</Button>
      </div>
    </div>

	<!-- Custom left-click context menu -->
    <div v-if="menuVisible"
      :style="{
      position: 'fixed',
      top: menuY + 'px',
      left: menuX + 'px',
      background: '#fff',
      border: '1px solid #ccc',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      zIndex: 2000,
      minWidth: '120px',
    }"
    >
    <div v-if="props.enableCopy"
      @click="copyToClipboard"
      @mouseover="hoverCopyMenuItem = true"
      @mouseleave="hoverCopyMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverCopyMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.copy') }}
    </div>

	<div v-if="props.enableNotifications"
      @click="notifyStatus"
      @mouseover="hoverEmailMenuItem = true"
      @mouseleave="hoverEmailMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverEmailMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.sendemail') }}
    </div>

</div>

  </Card>
  
  <EditorPanel v-if="showEditor[props.endpoint]"
               :columns="editorColumnsForPanel"
			   :label="editorPanelTitle"
			   :readOnly="editorReadOnly"
			   :is-saving="!!editorSaving[String(props.endpoint)]"
			   :currentItem="currentItem[props.endpoint]"
			   :dynamicOptions="dynamicOptions"
			   endpoint="props.endpoint" 
			   @close-editor="entityStore.closeEditor(props.endpoint)"
			   @save-editor="entityStore.saveEditor(props.endpoint, props.saveendpoint || props.endpoint, () => {loadServerData();}, (k, v) => filterWriteable(k, v))"/>
</template>

<style scoped>
/* Table layout */
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

:deep(.richtext div) {
    max-height: 40px !important;
	overflow: inherit !important;
}
/* Sticky rows already handled with classes in template */

/* Optional row animation */
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(-10px); }
.fade-slide-enter-to { opacity: 1; transform: translateY(0); }
.fade-slide-leave-from { opacity: 1; transform: translateY(0); }
.fade-slide-leave-to { opacity: 0; transform: translateY(10px); }

/* Highlight new row */
.new-row { animation: highlight 1s ease; }
@keyframes highlight { 0% { background-color: #e0f2fe; } 100% { background-color: transparent; } }

td:has(.single-select) {
  overflow: visible; /* allow the dropdown to escape */
}

.th-label {
  overflow: hidden;
  white-space: normal;   /* allow wrapping */
  text-overflow: ellipsis; /* only works if combined with height/line-clamp */
  max-height: 2rem;       /* for example, 3 lines of text */
}

.datatable-spinner {
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: rgba(0, 0, 0, 0.55);
  animation: datatable-spin 0.8s linear infinite;
}

@keyframes datatable-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
